import React, { useState, useContext, useEffect } from 'react';
import {
  Plus, Trash2, Crown, Star, Zap, Users, Briefcase, Globe,
  Check, X, Edit2, Layers, ChevronRight, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import AppContext from '../../../../context/AppContext';
import '../../../../../src/assets/css/screens/admin/dashboard/subscriptions/Subscription.css';

// ─── Constants ───────────────────────────────────────────────
const DURATIONS = ['Monthly', 'Quarterly', 'Yearly'];
const AUDIENCES = ['Customers', 'Workers', 'Both'];
const TIERS     = ['Basic', 'Pro', 'Elite'];

const TIER_ICON = {
  Basic: <Zap  size={20} />,
  Pro:   <Star size={20} />,
  Elite: <Crown size={20} />,
};

const TIER_CLASS = {
  Basic: 'sub-card--basic',
  Pro:   'sub-card--pro',
  Elite: 'sub-card--elite',
};

const TIER_BADGE_CLASS = {
  Basic: 'sub-tier-badge sub-tier-badge--basic',
  Pro:   'sub-tier-badge sub-tier-badge--pro',
  Elite: 'sub-tier-badge sub-tier-badge--elite',
};

// ─── Audience Icon ────────────────────────────────────────────
const AudienceIcon = ({ audience }) => {
  if (audience === 'Workers') return <><Briefcase size={12} /> Workers</>;
  if (audience === 'Both')    return <><Globe     size={12} /> Both</>;
  return                             <><Users     size={12} /> Customers</>;
};

// ─── Empty Feature ────────────────────────────────────────────
const emptyFeature = () => ({
  feature_name: "",
  feature_value: ""
});
// ─── Component ───────────────────────────────────────────────
const AdminSubscription = () => {
  const { baseUrl, authToken } = useContext(AppContext);

  const [plans,      setPlans]      = useState([]);
  const [showModal,  setShowModal]  = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [error,      setError]      = useState('');

  // form state
  const [planName,  setPlanName]  = useState('');
  const [price,     setPrice]     = useState('');
  const [duration,  setDuration]  = useState('Monthly');
  const [audience,  setAudience]  = useState('Customers');
  const [tier,      setTier]      = useState('Basic');
  const [features,  setFeatures]  = useState([emptyFeature()]);

  // ── Modal helpers ──────────────────────────────────────────
  const resetForm = () => {
    setPlanName(''); setPrice(''); setDuration('Monthly');
    setAudience('Customers'); setTier('Basic');
    setFeatures([emptyFeature()]); setEditingId(null); setError('');
  };

  const openModal = () => { resetForm(); setShowModal(true); };

  const closeModal = () => { setShowModal(false); resetForm(); };

  const openEditModal = (plan) => {
    setPlanName(plan.planName); setPrice(plan.price);
    setDuration(plan.duration); setAudience(plan.audience); setTier(plan.tier);
setFeatures(plan.features?.length ? plan.features : [emptyFeature()]);
    setEditingId(plan.id); setError(''); setShowModal(true);
  };

  // ── Feature helpers ────────────────────────────────────────
  const addFeature    = () => setFeatures(f => [...f, emptyFeature()]);
  const removeFeature = (i) => setFeatures(f => f.filter((_, idx) => idx !== i));
  const handleFeatureChange = (i, field, val) => {
    setFeatures(f => { const u = [...f]; u[i] = { ...u[i], [field]: val }; return u; });
  };

  // ── API ────────────────────────────────────────────────────
  const viewAllPlans = () => {
    axios.get(`${baseUrl}/view/subscription`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
    .then(r  => { setPlans(r.data.sub); })
    .catch(e => console.log(e));
  };

  const buildFormData = () => {
    const fd = new FormData();
    if (editingId) fd.append('id', editingId);
    fd.append('planName', planName);
    fd.append('price',    price);
    fd.append('duration', duration);
    fd.append('audience', audience);
    fd.append('tier',     tier);
    fd.append('features', JSON.stringify(features));
    return fd;
  };

  const handleAdd = () => {
    if (!planName || !price || !features.length) {
      setError('Please fill all required fields.'); return;
    }
    setLoading(true); setError('');
    axios.post(`${baseUrl}/create/subscription`, buildFormData(), {
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
    })
    .then(() => { viewAllPlans(); setLoading(false); closeModal(); })
    .catch(() => { setLoading(false); setError('Network error. Please try again.'); });
  };

  const handleEdit = () => {
    if (!planName || !price || !features.length) {
      setError('Please fill all required fields.'); return;
    }
    setLoading(true); setError('');
    axios.post(`${baseUrl}/update/subscription`, buildFormData(), {
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
    })
    .then(() => { viewAllPlans(); setLoading(false); closeModal(); })
    .catch(() => { setLoading(false); setError('Network error. Please try again.'); });
  };

  const handleDelete = (id) => {
    const fd = new FormData(); fd.append('id', id);
    axios.post(`${baseUrl}/delete/subscription`, fd, {
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
    })
    .then(()  => viewAllPlans())
    .catch(e => console.log(e));
  };

  const FEATURE_OPTIONS = [
  "Bidding",
  "Bid Visibility",

];
  useEffect(() => { viewAllPlans(); }, []);

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="sub-page">

      {/* ── HEADER ─────────────────────────────────── */}
      <div className="sub-header">
        <div className="sub-header__left">
          <div className="sub-header__icon">
            <Layers size={22} />
          </div>
          <div>
            <h2 className="sub-title">Subscription Plans</h2>
            <p className="sub-subtitle">Create and manage premium plans for customers and workers</p>
          </div>
        </div>
        <button className="sub-add-btn" onClick={openModal}>
          <Plus size={15} /> Add New Plan
        </button>
      </div>

      {/* ── PLANS GRID ─────────────────────────────── */}
      {plans.length > 0 && (
        <div className="sub-section">
          <p className="sub-section__label">
            <span className="sub-section__count">{plans.length}</span> Active Plans
          </p>
          <div className="sub-grid">
            {plans.map((plan, i) => (
              <div
                key={plan.id}
                className={`sub-card ${TIER_CLASS[plan.tier] || 'sub-card--basic'}`}
                style={{ animationDelay: `${i * 0.07}s` }}
              >
                {/* top strip accent */}
                <div className="sub-card__strip" />

                {/* ACTION BUTTONS */}
                <div className="sub-card__actions">
                  <button className="sub-card__edit" onClick={() => openEditModal(plan)} title="Edit plan">
                    <Edit2 size={13} />
                  </button>
                  <button className="sub-card__del" onClick={() => handleDelete(plan.id)} title="Delete plan">
                    <Trash2 size={13} />
                  </button>
                </div>

                {/* ICON + TIER */}
                <div className="sub-card__top">
                  <div className="sub-card__icon">
                    {TIER_ICON[plan.tier] || <Star size={20} />}
                  </div>
                  <span className={TIER_BADGE_CLASS[plan.tier] || TIER_BADGE_CLASS.Basic}>
                    {plan.tier}
                  </span>
                </div>

                {/* NAME */}
                <h3 className="sub-card__name">{plan.planName}</h3>

                {/* PRICE */}
                <div className="sub-card__price">
                  <span className="sub-card__currency">$</span>
                  <span className="sub-card__price-val">{plan.price}</span>
                  <span className="sub-card__price-per">/ {plan.duration?.toLowerCase()}</span>
                </div>

                {/* AUDIENCE */}
                <div className="sub-card__audience">
                  <AudienceIcon audience={plan.audience} />
                </div>

                {/* DIVIDER */}
                <div className="sub-card__divider" />

                {/* FEATURES */}
                <ul className="sub-card__features">
                  {plan.features?.map((f, idx) => (
                    <li key={idx}>
                      <span className="sub-card__check"><Check size={12} /></span>
                      <span className="sub-card__feat-text">
                        <strong>{f.feature_name}</strong>
                        {f.feature_value && <span className="sub-card__feat-val"> — {f.feature_value}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── EMPTY STATE ───────────────────────────── */}
      {plans.length === 0 && (
        <div className="sub-empty">
          <div className="sub-empty__icon"><Crown size={36} /></div>
          <p className="sub-empty__title">No plans yet</p>
          <p className="sub-empty__desc">Add your first subscription plan to get started.</p>
          <button className="sub-add-btn sub-add-btn--sm" onClick={openModal}>
            <Plus size={14} /> Create First Plan
          </button>
        </div>
      )}

      {/* ── MODAL ────────────────────────────────── */}
      {showModal && (
        <div className="sub-overlay" onClick={closeModal}>
          <div className="sub-modal" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="sub-modal__header">
              <div className="sub-modal__header-left">
                <div className="sub-modal__header-icon">
                  {editingId ? <Edit2 size={15} /> : <Plus size={15} />}
                </div>
                <div>
                  <h3 className="sub-modal__title">
                    {editingId ? 'Edit Plan' : 'Add New Plan'}
                  </h3>
                  <p className="sub-modal__sub">
                    {editingId ? 'Update the plan details below' : 'Fill in the details to create a new plan'}
                  </p>
                </div>
              </div>
              <button className="sub-modal__close" onClick={closeModal} aria-label="Close">
                <X size={16} />
              </button>
            </div>

            {/* Error banner */}
            {error && (
              <div className="sub-error-banner">
                <AlertCircle size={14} />
                <span>{error}</span>
              </div>
            )}

            <div className="sub-modal__body">

              {/* ── Section: Plan Info ── */}
              <div className="sub-form-section">
                <p className="sub-form-section__label">
                  <ChevronRight size={13} /> Plan Info
                </p>
                <div className="sub-modal__row">
                  <div className="sub-field">
                    <label className="sub-label">
                      Plan Name <span className="sub-required">*</span>
                    </label>
                    <input
                      className="sub-input"
                      placeholder="e.g. Premium Plus"
                      value={planName}
                      onChange={e => setPlanName(e.target.value)}
                    />
                  </div>
                  <div className="sub-field">
                    <label className="sub-label">
                      Price (USD) <span className="sub-required">*</span>
                    </label>
                    <div className="sub-input-wrap">
                      <span className="sub-input-prefix">$</span>
                      <input
                        className="sub-input sub-input--prefixed"
                        type="number"
                        placeholder="19.99"
                        min="0"
                        step="0.01"
                        value={price}
                        onChange={e => setPrice(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Section: Settings ── */}
              <div className="sub-form-section">
                <p className="sub-form-section__label">
                  <ChevronRight size={13} /> Settings
                </p>
                <div className="sub-modal__row sub-modal__row--3">
                  <div className="sub-field">
                    <label className="sub-label">Duration</label>
                    <select className="sub-select" value={duration} onChange={e => setDuration(e.target.value)}>
                      {DURATIONS.map(d => <option key={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="sub-field">
                    <label className="sub-label">Audience</label>
                    <select className="sub-select" value={audience} onChange={e => setAudience(e.target.value)}>
                      {AUDIENCES.map(a => <option key={a}>{a}</option>)}
                    </select>
                  </div>
                  <div className="sub-field">
                    <label className="sub-label">Tier</label>
                    <select className="sub-select" value={tier} onChange={e => setTier(e.target.value)}>
                      {TIERS.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* ── Section: Features ── */}
              <div className="sub-form-section">
                <div className="sub-form-section__head">
                  <p className="sub-form-section__label">
                    <ChevronRight size={13} /> Features
                  </p>
                  <button className="sub-add-feature-btn" onClick={addFeature} type="button">
                    <Plus size={13} /> Add Feature
                  </button>
                </div>

                <div className="sub-feature-container">
                  {features.map((feature, index) => (
                    <div className="sub-feature-row" key={index}>
                      <div className="sub-feature-num">{index + 1}</div>

                      <div className="sub-feature-fields">
                        <div className="sub-feature-field-wrap">
                          <span className="sub-feature-field-label">Name</span>
                         <select
  className="sub-feature-input"
  value={feature.feature_name}
  onChange={e =>
    handleFeatureChange(index, "feature_name", e.target.value)
  }
>
  <option value="">Select Feature</option>

  {FEATURE_OPTIONS.map((item) => (
    <option key={item} value={item}>
      {item}
    </option>
  ))}
</select>
                        </div>
                        {/* <div className="sub-feature-field-wrap">
                          <span className="sub-feature-field-label">Key</span>
                          <input
                            className="sub-feature-input"
                            placeholder="e.g. max_bookings"
                            value={feature.feature_key}
                            onChange={e => handleFeatureChange(index, 'feature_key', e.target.value)}
                          />
                        </div> */}
                        <div className="sub-feature-field-wrap">
                          <span className="sub-feature-field-label">Value</span>
                          <input
                            className="sub-feature-input"
                            placeholder="e.g. 50"
                            value={feature.feature_value}
                            onChange={e => handleFeatureChange(index, 'feature_value', e.target.value)}
                          />
                        </div>
                      </div>

                      <button
                        className="sub-feature-delete"
                        onClick={() => removeFeature(index)}
                        type="button"
                        title="Remove feature"
                        disabled={features.length === 1}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

            </div>{/* end body */}

            {/* Footer */}
            <div className="sub-modal__footer">
              <button className="sub-modal__cancel" onClick={closeModal}>Cancel</button>
              <button
                className="sub-modal__submit"
                onClick={editingId ? handleEdit : handleAdd}
                disabled={loading}
              >
                {loading
                  ? (editingId ? 'Updating…' : 'Adding…')
                  : editingId
                    ? <><Edit2 size={14} /> Update Plan</>
                    : <><Plus  size={14} /> Add Plan</>
                }
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default AdminSubscription;